import { type ComputedRef, computed, type Ref, ref } from 'vue';
import { store } from '@/app/store';
import { analytics } from '@/services/Analytics';
import { printing } from '@/services/Printing';
import { sentryService } from '@/services/telemetry/sentry';
import { track } from '@/shared/utils/track-telemetry';
import type { PortraitLike } from './useParallelPortraitSlots';
import { readPortraitSessionContext } from './usePortraitTelemetry';

interface UsePortraitPrintSelectionOptions {
  getSourcePortrait: (
    portrait: PortraitLike | null | undefined
  ) => PortraitLike | null;
}

export interface PortraitPrintSelection {
  getSelectedPortraitCount: (item: PortraitLike) => number | undefined;
  hasAnyPortraitsSelected: ComputedRef<boolean>;
  hasOnlyGroupPortraits: ComputedRef<boolean>;
  onPortraitCountDecrement: (event: Event, item: PortraitLike) => void;
  onPortraitCountIncrement: (event: Event, item: PortraitLike) => void;
  onPortraitSelectionToggle: (
    event: Event,
    item: PortraitLike,
    isSelected: boolean
  ) => void;
  restoreSelectionFromPrintingQueue: () => void;
  selectedPortraitUuids: Ref<string[]>;
}

export function usePortraitPrintSelection(
  options: UsePortraitPrintSelectionOptions
): PortraitPrintSelection {
  const selectedPortraitUuids = ref<string[]>([]);
  const hasAnyPortraitsSelected = computed(
    () => selectedPortraitUuids.value.length > 0 || store.printingTotalCount > 0
  );
  const hasOnlyGroupPortraits = computed(
    () =>
      store.printingTotalCount > 0 && selectedPortraitUuids.value.length === 0
  );

  function updateSessionContext(): void {
    track('[Portraits] Context update error:', () => {
      const context = readPortraitSessionContext(
        selectedPortraitUuids.value.length
      );
      sentryService.updateSessionContext({
        portraits_total: context.totalPortraits,
        portraits_preloaded: context.preloadedPortraits,
        portraits_errors: context.errorPortraits,
        portraits_selected: context.selectedPortraits,
      });
    });
  }

  function updateGalleryModel(uuid: string, isSelected: boolean): void {
    selectedPortraitUuids.value = isSelected
      ? Array.from(new Set([...selectedPortraitUuids.value, uuid]))
      : selectedPortraitUuids.value.filter(
          (selectedUuid) => selectedUuid !== uuid
        );
  }

  function restoreSelectionFromPrintingQueue(): void {
    const currentPortraitUuids = new Set(
      store.portraits.map((portrait) => portrait.uuid)
    );
    const restoredUuids = printing.queue
      .filter(
        (item) => !item.isGroupPortrait && currentPortraitUuids.has(item.uuid)
      )
      .map((item) => item.uuid);

    if (restoredUuids.length === 0) {
      return;
    }

    selectedPortraitUuids.value = restoredUuids;
    track('[Portraits] Breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'ui.state',
        'Restored portrait selection from queue',
        { restoredCount: restoredUuids.length }
      );
    });
  }

  function onPortraitSelectionToggle(
    _event: Event,
    item: PortraitLike,
    isSelected: boolean
  ): void {
    const portrait = options.getSourcePortrait(item);
    if (!portrait) {
      return;
    }

    if (isSelected) {
      printing.add(portrait.uuid);
    } else {
      printing.remove(portrait.uuid);
    }
    analytics.trackTimeTo(analytics.METRICS.time_portraits_picked, {});

    track('[Portraits] Selection tracking error:', () => {
      sentryService.addBreadcrumb(
        'portrait.selection',
        isSelected
          ? 'Portrait selected for printing'
          : 'Portrait deselected from printing',
        {
          portraitUuid: portrait.uuid,
          action: isSelected ? 'select' : 'deselect',
          currentSelectedCount: isSelected
            ? selectedPortraitUuids.value.length + 1
            : selectedPortraitUuids.value.length - 1,
        }
      );
      sentryService.incrementCounter(
        isSelected ? 'portraits.selections' : 'portraits.deselections',
        1,
        { type: 'individual' }
      );
    });

    updateGalleryModel(portrait.uuid, isSelected);
    updateSessionContext();
  }

  function onPortraitCountIncrement(event: Event, item: PortraitLike): void {
    const portrait = options.getSourcePortrait(item);
    event.preventDefault();
    event.stopPropagation();
    if (!portrait) {
      return;
    }

    const previousCount = getSelectedPortraitCount(portrait) || 0;
    printing.increment(portrait.uuid);
    updateGalleryModel(portrait.uuid, true);

    track('[Portraits] Quantity breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'portrait.quantity',
        'Portrait quantity increased',
        {
          portraitUuid: portrait.uuid,
          previousCount,
          newCount: previousCount + 1,
        }
      );
    });
  }

  function onPortraitCountDecrement(event: Event, item: PortraitLike): void {
    const portrait = options.getSourcePortrait(item);
    event.preventDefault();
    event.stopPropagation();
    if (!portrait) {
      return;
    }

    const previousCount = getSelectedPortraitCount(portrait) || 0;
    const updatedPortrait = printing.decrement(portrait.uuid);
    const isStillSelected = Boolean(updatedPortrait?.count);
    updateGalleryModel(portrait.uuid, isStillSelected);

    track('[Portraits] Quantity breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'portrait.quantity',
        'Portrait quantity decreased',
        {
          portraitUuid: portrait.uuid,
          previousCount,
          newCount: isStillSelected ? previousCount - 1 : 0,
          removed: !isStillSelected,
        }
      );
    });

    if (!isStillSelected) {
      updateSessionContext();
    }
  }

  function getSelectedPortraitCount(item: PortraitLike): number | undefined {
    const portrait = options.getSourcePortrait(item);
    return portrait ? printing.getByUuid(portrait.uuid)?.count : 0;
  }

  return {
    getSelectedPortraitCount,
    hasAnyPortraitsSelected,
    hasOnlyGroupPortraits,
    onPortraitCountDecrement,
    onPortraitCountIncrement,
    onPortraitSelectionToggle,
    restoreSelectionFromPrintingQueue,
    selectedPortraitUuids,
  };
}
